import Cookies from "js-cookie";
import { authFetch } from "../../utils/api";

/**
 * Returns a flat permissions object based on the priority chain:
 *   1. crmDefined (external CRM e.g. Zoho) — external CRM owns customers/quotes
 *   2. simpleCrmEnabled — built-in CRM rules apply
 *   3. Default — user manages their own stuff, system admins see all
 *
 * @param {object} ctx
 *   crmUserId        — from get_crm_user_id() e.g. "NA" or "123"
 *   crmRole          — crm_role cookie: '' | 'sales' | 'manager'
 *   userRole         — user_role cookie: 'admin' | 'superadmin' | 'manager' | 'sales' | ...
 *   userId           — user_id cookie
 *   quoteManagerOnly — crmSettings.quote_manager_only
 *   commonCustomers  — crmSettings.common_customers
 *   crmDefined       — external CRM active (from DB crm_settings.external_crm != 'none')
 *   simpleCrmEnabled — Simple CRM active (from DB crm_settings.simple_crm_enabled)
 *
 * @returns {object} permissions
 *   canAddCustomer      — show Add Customer button
 *   canEditCustomer     — show Edit Customer button
 *   canDeleteCustomer   — show Delete Customer button
 *   canAddQuote         — show New Quote button
 *   canEditQuote        — show Edit Quote button
 *   canDeleteQuote      — show Delete Quote (row action)
 *   seeAllQuotes        — fetch all quotes (vs own only)
 *   seeAllCustomers     — fetch all customers (vs own only)
 *   quotesUrl           — ready-made API URL fragment for quotes fetch
 *   isCrmManager        — user is a SimpleCRM manager
 *   isCrmSales          — user is a SimpleCRM sales rep
 */
export function getPermissions({
  crmUserId, crmRole, userRole, userId,
  quoteManagerOnly, commonCustomers,
  crmDefined = false, simpleCrmEnabled = false,
}) {
  const isSystemAdmin = ['admin', 'superadmin', 'manager'].includes(userRole);
  const isDealerRole  = userRole === 'dealer';
  const isOwnerRole   = userRole === 'owner';
  const isZohoCrmUser = crmDefined && crmUserId && crmUserId !== "NA";

  // ── 0. Dealer-scoped roles — always take priority over CRM settings ─────────
  if (isOwnerRole) {
    return {
      canAddCustomer:    false,
      canEditCustomer:   false,
      canDeleteCustomer: false,
      canAddQuote:       false,
      canEditQuote:      false,
      canDeleteQuote:    false,
      seeAllQuotes:      true,
      seeAllCustomers:   true,
      quotesUrl:         '/quotes/',
      isCrmManager:      false,
      isCrmSales:        false,
      seeUserColumn:     true,
      seeDealerColumn:   true,
    };
  }

  if (isDealerRole) {
    return {
      canAddCustomer:    false,
      canEditCustomer:   false,
      canDeleteCustomer: false,
      canAddQuote:       false,
      canEditQuote:      false,
      canDeleteQuote:    false,
      seeAllQuotes:      true,
      seeAllCustomers:   true,
      quotesUrl:         '/quotes/',
      isCrmManager:      false,
      isCrmSales:        false,
      seeUserColumn:     true,
      seeDealerColumn:   false,
    };
  }

  // ── 1. External CRM (e.g. Zoho) ────────────────────────────────────────────
  if (crmDefined) {
    if (isZohoCrmUser) {
      // External CRM owns customers & quotes — view only, no add/edit/delete
      return {
        canAddCustomer:    false,
        canEditCustomer:   false,
        canDeleteCustomer: false,
        canAddQuote:       false,
        canEditQuote:      false,
        canDeleteQuote:    false,
        seeAllQuotes:      false,
        seeAllCustomers:   false,
        quotesUrl:         `/quotes/quotesbyuser/?fkUserId=${userId}`,
        isCrmManager:      false,
        isCrmSales:        false,
        isZohoUser:        true,
        seeUserColumn:     false,
        seeDealerColumn:   false,
      };
    }
    // crmDefined but this user has no external CRM ID → fall through
  }

  // ── 2. Simple CRM ──────────────────────────────────────────────────────────
  if (simpleCrmEnabled) {
    const isCrmManager = crmRole === 'manager';
    const isCrmSales   = crmRole === 'sales';

    if (isCrmManager) {
      return {
        canAddCustomer:    true,
        canEditCustomer:   true,
        canDeleteCustomer: true,
        canAddQuote:       true,
        canEditQuote:      true,
        canDeleteQuote:    true,
        seeAllQuotes:      true,
        seeAllCustomers:   true,
        quotesUrl:         '/quotes/',
        isCrmManager:      true,
        isCrmSales:        false,
        seeUserColumn:     false,
        seeDealerColumn:   false,
      };
    }

    if (isCrmSales) {
      const noQuoteControl    = quoteManagerOnly || commonCustomers;
      const noCustomerControl = commonCustomers;
      return {
        canAddCustomer:    !noCustomerControl,
        canEditCustomer:   !noCustomerControl,
        canDeleteCustomer: !noCustomerControl,
        canAddQuote:       !noQuoteControl,
        canEditQuote:      !noQuoteControl,
        canDeleteQuote:    !noQuoteControl,
        seeAllQuotes:      false,
        seeAllCustomers:   false,
        quotesUrl:         `/quotes/quotesbyuser/?fkUserId=${userId}`,
        isCrmManager:      false,
        isCrmSales:        true,
        seeUserColumn:     false,
        seeDealerColumn:   false,
      };
    }

    // simpleCrmEnabled but user has no crm_role — fall through to default
  }

  // ── 3. Default — no CRM ────────────────────────────────────────────────────
  if (isSystemAdmin) {
    return {
      canAddCustomer:    true,
      canEditCustomer:   true,
      canDeleteCustomer: true,
      canAddQuote:       true,
      canEditQuote:      true,
      canDeleteQuote:    true,
      seeAllQuotes:      true,
      seeAllCustomers:   true,
      quotesUrl:         '/quotes/',
      isCrmManager:      false,
      isCrmSales:        false,
      seeUserColumn:     false,
      seeDealerColumn:   false,
    };
  }

  return {
    canAddCustomer:    true,
    canEditCustomer:   true,
    canDeleteCustomer: true,
    canAddQuote:       true,
    canEditQuote:      true,
    canDeleteQuote:    true,
    seeAllQuotes:      false,
    seeAllCustomers:   false,
    quotesUrl:         `/quotes/quotesbyuser/?fkUserId=${userId}`,
    isCrmManager:      false,
    isCrmSales:        false,
    seeUserColumn:     false,
    seeDealerColumn:   false,
  };
}


export async function get_crm_user_id() {
  let crmUserId = Cookies.get("crm_user_id");

  if (crmUserId) {
    return crmUserId;
  }

  const user_id = Cookies.get("user_id");
  if (!user_id) {
    console.error("Missing user_id cookie.");
    return "NA";
  }

  try {
    const userData = await authFetch(`/users/${user_id}`);
    crmUserId = userData?.crm_user_id || "NA";
    Cookies.set("crm_user_id", crmUserId, { path: "/" });
    return crmUserId;
  } catch (err) {
    console.error("Error fetching CRM user ID:", err);
    return "NA";
  }
}
